import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "../../api";
import { getApiBase } from '../../config/env';
import { Container } from "../layout/Container"
import { PageHeader } from "../layout/PageHeader"
import { Button } from "../ui/button"
import { Badge } from "../ui/badge"
import { toast } from "../shared/ToastProvider";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";

const StocktwitsBatchImportForm = () => {
  const [selectedStock, setSelectedStock] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [pagesMax, setPagesMax] = useState("");
  const [cursorStart, setCursorStart] = useState("");
  const [availableTickers, setAvailableTickers] = useState([]);
  const [, setLiveLogs] = useState({});
  const [lastCursorInfo, setLastCursorInfo] = useState(null);
  const [useLastCursor, setUseLastCursor] = useState(false);
  const [cursorInfosByTicker, setCursorInfosByTicker] = useState({});
  const [importStatuses, setImportStatuses] = useState({});
  const [formErrors, setFormErrors] = useState({ startDate: "", endDate: "", cursor: "" });
  const [formWarnings, setFormWarnings] = useState([]);




  useEffect(() => {
    const fetchTickers = async () => {
      try {
        const response = await api.get("/stocks");
        const symbols = response.data.map((s) => s.stock_symbol);
        setAvailableTickers(symbols);
      } catch (error) {
        console.error("Erreur lors de la récupération des tickers", error);
      }
    };
    fetchTickers();
  }, []);

  useEffect(() => {
    const BASE = getApiBase();
    const eventSource = new EventSource(`${BASE}/api/import-status-stream`);
    eventSource.onmessage = (event) => {
      try {
        const { ticker, message } = JSON.parse(event.data);


        setLiveLogs((prev) => {
          const current = prev[ticker] || [];
          return {
            ...prev,
            [ticker]: [...current.slice(-20), message],
          };
        });
      } catch (err) {
        console.error("Erreur parsing SSE :", err);
      }
    };

    eventSource.onerror = (err) => {
      console.error("❌ SSE error:", err);
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, []);





  // 🎯 Quand l'import se termine (status 200), on récupère le curseur final
  useEffect(() => {
    const statusForThisStock = Object.values(importStatuses).find(
      s => s.ticker === selectedStock
    )?.http_status;

    if (statusForThisStock === 200) {
      api
        .get(`/stocks/last-cursor-id?ticker=${selectedStock}`)
        .then((res) => {
          if (res.data?.lastCursorImportedId) {
            setLastCursorInfo(res.data);
          }
        })
        .catch((err) => {
          console.warn("❌ Impossible de récupérer le curseur :", err.message);
        });
    }
  }, [importStatuses, selectedStock]);


  const tickers = useMemo(() => {
    return [...new Set([...availableTickers, selectedStock])];
  }, [availableTickers, selectedStock]);

  const batchTickers = useMemo(() => {
    return tickers.filter(
      (t) => typeof t === "string" && t.trim().length > 0
    );
  }, [tickers]);




  const fetchAllCursorInfo = useCallback(async () => {
    const newInfos = {};

    // BUGFIX EN MAJ : BOUCLER SUR batchTickers (LISTE SANS "" ET SANS ESPACES)
    for (const ticker of batchTickers) {
      try {
        const [cursorRes, logRes] = await Promise.all([
          api.get(`/stocks/last-cursor-id?ticker=${ticker}`),
          api.get(`/importlogs/last-finish?ticker=${ticker}`)
        ]);

        // BUGFIX EN MAJ : ENREGISTRER MEME SI lastCursorImportedId EST NULL
        newInfos[ticker] = {
          lastCursorImportedId: cursorRes.data?.lastCursorImportedId ?? null,
          lastInsertedDate: cursorRes.data?.lastInsertedDate ?? null,
          finishedAt: logRes.data?.finishedAt || null,
        };
      } catch (err) {
        console.warn(`❌ Échec récupération infos pour ${ticker} : ${err.message}`);
        // on garde une valeur vide pour éviter une cellule "vide"
        newInfos[ticker] = {
          lastCursorImportedId: null,
          lastInsertedDate: null,
          finishedAt: null,
        };
      }
    }

    setCursorInfosByTicker(newInfos);
  }, [batchTickers]);

  const fetchStatuses = useCallback(async () => {
    try {
      const res = await api.get("/importlogs/statuses");
      setImportStatuses(res.data);
    } catch (e) {
      console.warn("Erreur récupération http_status", e);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    await fetchAllCursorInfo();
    await fetchStatuses();
  }, [fetchAllCursorInfo, fetchStatuses]);

  useEffect(() => {
    // BUGFIX EN MAJ : CHARGER LES CURSEURS AUTOMATIQUEMENT DES QUE LES TICKERS SONT PRETS
    if (batchTickers.length === 0) return;
    fetchAllCursorInfo();
  }, [batchTickers, fetchAllCursorInfo]);

  // ⚡ déclenchement initial automatique
  //}, [tickers]);

  const handleCustomImport = async () => {
    const warnings = [];
    if (startDate && endDate) {
      const diff = (new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24);
      if (diff > 90) warnings.push("⛔ La période dépasse 90 jours !");
    }
    if (!cursorStart) warnings.push("⚠️ Aucun curseur de départ défini.");
    if (pagesMax && parseInt(pagesMax) > 50) warnings.push("⚠️ PagesMax > 50");
    setFormWarnings(warnings);

    let finalCursorStart = cursorStart;

    if (useLastCursor) {
      try {
        const response = await api.get(`/stocks/last-cursor-id?ticker=${selectedStock}`);
        if (response.data?.lastCursorImportedId) {
          finalCursorStart = response.data.lastCursorImportedId;
        } else {
          setFormErrors(prev => ({ ...prev, cursor: "Aucun curseur trouvé pour ce stock." }));
          return;
        }
      } catch (error) {
        toast.error("Impossible de récupérer le curseur.");
        return;
      }
    }

    try {
      await api.post("/api/import-one-stock", {
        ticker: selectedStock,
        startDate,
        endDate,
        pagesMax: pagesMax ? parseInt(pagesMax) : undefined,
        cursorStart: finalCursorStart ? parseInt(finalCursorStart) : undefined,
      });
      toast.success("Import lancé avec succès");
    } catch (error) {
      console.error("❌ Erreur import :", error);
      toast.error("Erreur lors de l'importation");
    }

    // 🔍 Récupérer le dernier curseur inséré

    // Réinitialise l'affichage du dernier curseur
    setLastCursorInfo(null);
  };

  const handleBatchImport = async () => {
    const errors = {};
    if (!startDate) errors.startDate = "Requis";
    if (!endDate) errors.endDate = "Requis";
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      toast.error("Veuillez renseigner les dates de début et de fin.");
      return;
    }

    try {
      await api.post("/api/batch-import", {
        tickers: batchTickers,
        startDate,
        endDate
      });
      toast.success("Import batch lancé");
    } catch (error) {
      console.error("Erreur globale lors de l'import :", error);
      toast.error("Erreur lors du lancement du batch");
    }
  };

  const renderHttpStatus = (code) => {
    if (!code) return "⏸️ En attente";

    if (code === 202) return "⏳ En cours";
    if (code === 200) return "✅ Succès";
    if (code === 500) return "❌ Erreur";
    if (code === 503) return "🔁 Réessai";
    if (code === 100) return "ℹ️ Info";

    return code;
  };

  const getBadgeVariantForStatus = (code) => {
    if (!code) return "outline";
    if (code === 202) return "default";
    if (code === 200) return "secondary";
    if (code === 500) return "destructive";
    if (code === 503) return "outline";
    if (code === 100) return "secondary";
    return "outline";
  };

    return (
    <Container className="py-6 md:py-10 bg-background rounded-lg border border-input shadow-sm space-y-10">
      <PageHeader title="Import Stocktwits" />

      {/* -------------- Importation personnalisée (1 seul stock) -------------- */}
      <div>
        <h2 className="text-lg font-bold mb-4">Importation personnalisée (1 seul stock)</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-start">
          <div>
            <label className="block font-medium mb-1">Stock</label>
            <select
              className="border border-input bg-background text-foreground p-2 rounded w-full"
              value={selectedStock}
              onChange={(e) => setSelectedStock(e.target.value)}
            >
              {availableTickers.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block font-medium mb-1">Date de début</label>
            <input
              type="date"
              className={`border border-input bg-background text-foreground p-2 rounded w-full ${formErrors.startDate ? "border-destructive" : ""}`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            {formErrors.startDate && <p className="text-sm text-destructive mt-1">{formErrors.startDate}</p>}
          </div>
          <div>
            <label className="block font-medium mb-1">Date de fin</label>
            <input
              type="date"
              className={`border border-input bg-background text-foreground p-2 rounded w-full ${formErrors.endDate ? "border-destructive" : ""}`}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            {formErrors.endDate && <p className="text-sm text-destructive mt-1">{formErrors.endDate}</p>}
          </div>
          <div>
            <label className="block font-medium mb-1">Pages max (optionnel)</label>
            <input
              type="number"
              className="border border-input bg-background text-foreground p-2 rounded w-full"
              value={pagesMax}
              onChange={(e) => setPagesMax(e.target.value)}
            />
          </div>
          <div>
            <label className="block font-medium mb-1">Cursor Start ID (optionnel)</label>
            <input
              type="number"
              className={`border border-input bg-background text-foreground p-2 rounded w-full ${formErrors.cursor ? "border-destructive" : ""}`}
              value={cursorStart}
              onChange={(e) => setCursorStart(e.target.value)}
            />
            {formErrors.cursor && <p className="text-sm text-destructive mt-1">{formErrors.cursor}</p>}
          </div>
        </div>

        <div>
          <label className="flex items-center space-x-2 mt-2">
            <input
              type="checkbox"
              checked={useLastCursor}
              onChange={(e) => setUseLastCursor(e.target.checked)}
            />
            <span>Reprendre depuis le dernier curseur inséré</span>
          </label>
        </div>

        {useLastCursor && (
          <div className="text-xs text-blue-500 mt-1">
            L’import reprendra automatiquement depuis le dernier message stocké pour ce ticker.
          </div>
        )}

        {formWarnings.length > 0 && (
          <Alert variant="destructive" className="mt-4 mb-2">
            <AlertTitle>Attention</AlertTitle>
            <AlertDescription>
              <ul className="list-disc pl-5 mt-1 space-y-1">
                {formWarnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        <Button onClick={handleCustomImport} className="mt-4">
          Importer ce stock
        </Button>

        <div className="mt-2 flex items-center gap-2">
          <span className="text-sm text-muted-foreground">État :</span>
          <Badge variant={getBadgeVariantForStatus(
            Object.values(importStatuses).find(s => s.ticker === selectedStock)?.http_status
          )}>
            {renderHttpStatus(
              Object.values(importStatuses).find(s => s.ticker === selectedStock)?.http_status
            )}
          </Badge>
        </div>

        {lastCursorInfo !== null && (
          <div className="mt-4 p-2 rounded bg-green-500/10 text-green-500 border border-green-500/30 text-sm">
            ✅ Import terminé – Dernier curseur inséré :{" "}
            <strong>{lastCursorInfo.lastCursorImportedId}</strong> à{" "}
            {new Date(lastCursorInfo.lastInsertedDate).toLocaleString("fr-FR", {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </div>
        )}
      </div>

      {/* -------------- Importation Stocktwits (Batch) -------------- */}
      <div>
        <h2 className="text-lg font-bold mb-4">Importation Stocktwits (Batch)</h2>
        <div className="flex gap-4 flex-wrap mb-4">
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={`border border-input bg-background text-foreground p-2 rounded ${formErrors.startDate ? "border-destructive" : ""}`} />
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={`border border-input bg-background text-foreground p-2 rounded ${formErrors.endDate ? "border-destructive" : ""}`} />
          <Button variant="destructive" onClick={handleBatchImport}>
            Lancer import parallèle
          </Button>
        </div>

        <div className="mb-2">
          <Button
            onClick={handleRefresh}
            size="sm"
            className="text-sm bg-muted hover:bg-muted/80 text-foreground"
          >
            ↻ Rafraîchir les curseurs
          </Button>
        </div>

        <div className="relative w-full overflow-auto rounded-md border">
          <table className="w-full caption-bottom text-sm">
            <thead className="[&_tr]:border-b bg-muted/50">
              <tr>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Ticker</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">État</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">Curseur</th>
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {/* BUGFIX EN MAJ : UTILISER LA LISTE NETTOYÉE POUR ÉVITER UNE LIGNE VIDE (ticker="") */}
              {batchTickers.map((ticker) => (
                <tr key={ticker} className="border-b transition-colors hover:bg-muted/50">
                  <td className="p-4 align-middle font-mono">{ticker}</td>
                  <td className="p-4 align-middle whitespace-pre-line">
                    <Badge variant={getBadgeVariantForStatus(
                      Object.values(importStatuses).find(s => s.ticker === ticker)?.http_status
                    )}>
                      {renderHttpStatus(
                        Object.values(importStatuses).find(s => s.ticker === ticker)?.http_status
                      )}
                    </Badge>
                  </td>
                  <td className="p-4 align-middle font-mono">
                    {cursorInfosByTicker[ticker]?.lastCursorImportedId && (
                        <div className="text-xs text-green-500 mt-1">
                        Dernier curseur : <strong>{cursorInfosByTicker[ticker].lastCursorImportedId}</strong><br />
                        à{" "}
                        {new Date(cursorInfosByTicker[ticker].lastInsertedDate).toLocaleString("fr-FR", {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}<br />
                        {cursorInfosByTicker[ticker]?.finishedAt && (
                          <>
                            Import terminé à :{" "}
                            <strong>
                              {new Date(cursorInfosByTicker[ticker].finishedAt).toLocaleString("fr-FR", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })}
                            </strong>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Container>
  );
};

export default StocktwitsBatchImportForm;

